/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Dispatch } from "react";
import { Text } from "@nordcloud/gnui";
import { Mode } from "~/components";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { Item } from "~/components/SidebarWithMultiSelect/types";
import { isNotEmpty, isNotNil } from "~/tools";
import { ResourceUpdateStatus } from "~/views/resources/ResourceUpdateStatus/types";
import { State } from "./types";

type Props = {
  state: State;
  updateState: Dispatch<Partial<State>>;
};

const resourcePingStatusCheckboxes: Item[] = [
  {
    value: "Online",
    label: "Online",
    subLabel: "Available resources",
  },
  {
    value: "ConnectionLost",
    label: "Lost Connection",
    subLabel: "Resources that have lost connection or are unavailable",
  },
];

const lastUpdateStatusCheckboxes: Item[] = [
  {
    value: ResourceUpdateStatus.PendingUpdates,
    label: "Pending Updates",
  },
  {
    value: ResourceUpdateStatus.Failed,
    label: "Action Failed",
  },
  {
    value: ResourceUpdateStatus.NoUpdatesData,
    label: "No Updates Data",
  },
  {
    value: ResourceUpdateStatus.InProgress,
    label: "Action in Progress",
  },
  {
    value: ResourceUpdateStatus.NoPendingUpdates,
    label: "No Pending Updates",
  },
];

export function ResourceStatusFilters({ state, updateState }: Props) {
  const clearResourcePingStatusesFilter = () => {
    updateState({
      resourcePingStatuses: undefined,
      excludeResourcePingStatuses: undefined,
    });
  };

  const clearLastUpdateStatusesFilter = () => {
    updateState({
      lastUpdateStatuses: undefined,
      excludeLastUpdateStatuses: undefined,
    });
  };

  return (
    <>
      <Text size="md" weight="medium">
        Resource Status
      </Text>
      <FilterWrapper
        name="Resource Ping Status"
        isSelected={
          isNotNil(state.resourcePingStatuses) &&
          isNotEmpty(state.resourcePingStatuses)
        }
        selectedOptions={state.resourcePingStatuses ?? []}
        onClear={clearResourcePingStatusesFilter}
      >
        <SelectableListWithSearch
          allowModeChange
          selectedOptions={state.resourcePingStatuses ?? []}
          selectableItems={resourcePingStatusCheckboxes}
          isExcludeSelected={state.excludeResourcePingStatuses}
          onMultiSelect={(resourcePingStatuses) =>
            updateState({ resourcePingStatuses })
          }
          onModeChange={(mode) =>
            updateState({
              excludeResourcePingStatuses: mode === Mode.EXCLUDES,
            })
          }
        />
      </FilterWrapper>
      <FilterWrapper
        name="Last Update Status"
        isSelected={
          isNotNil(state.lastUpdateStatuses) &&
          isNotEmpty(state.lastUpdateStatuses)
        }
        selectedOptions={state.lastUpdateStatuses ?? []}
        onClear={clearLastUpdateStatusesFilter}
      >
        <SelectableListWithSearch
          allowModeChange
          selectedOptions={state.lastUpdateStatuses ?? []}
          selectableItems={lastUpdateStatusCheckboxes}
          isExcludeSelected={state.excludeLastUpdateStatuses}
          onMultiSelect={(lastUpdateStatuses) =>
            updateState({ lastUpdateStatuses })
          }
          onModeChange={(mode) =>
            updateState({ excludeLastUpdateStatuses: mode === Mode.EXCLUDES })
          }
        />
      </FilterWrapper>
    </>
  );
}
